import React from 'react';

interface DeviceFrameProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Phone chrome on small screens; landscape tablet rectangle on desktop.
 * Scenes scale via percentage / SVG viewBox.
 */
export default function DeviceFrame({ children, className = '' }: DeviceFrameProps) {
  return (
    <div
      className={`relative mx-auto w-[min(315px,92vw)] md:w-[clamp(440px,62vw,620px)] lg:w-[clamp(520px,46vw,680px)]
                  [--frame-r:3.1rem] [--frame-pad:0.35rem] sm:[--frame-r:3.35rem] sm:[--frame-pad:0.4rem] md:[--frame-r:2.35rem] md:[--frame-pad:0.55rem]
                  ${className}`}
    >
      {/* Soft ambient depth — restrained */}
      <div
        className="pointer-events-none absolute -inset-2 rounded-[calc(var(--frame-r)+0.9rem)] opacity-25 blur-xl md:-inset-3 md:opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at 50% 80%, rgba(15,23,42,0.18), transparent 65%)',
        }}
        aria-hidden
      />

      {/* Device body — screen radius = frame radius − bezel so corners stay concentric */}
      <div
        className="relative aspect-[9/15] overflow-hidden rounded-[var(--frame-r)] p-[var(--frame-pad)] md:aspect-[4/3]
                   bg-gradient-to-b from-zinc-600 via-zinc-800 to-zinc-950
                   shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_8px_20px_-6px_rgba(0,0,0,0.28),0_2px_6px_-2px_rgba(0,0,0,0.18)]
                   md:shadow-[0_1px_0_rgba(255,255,255,0.14)_inset,0_16px_36px_-10px_rgba(0,0,0,0.34),0_6px_14px_-4px_rgba(0,0,0,0.22)]"
      >
        {/* Top highlight reflection */}
        <div
          className="pointer-events-none absolute inset-x-4 top-0 z-20 h-10 rounded-b-full opacity-30 md:inset-x-8 md:h-8 md:opacity-40"
          style={{
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 100%)',
          }}
          aria-hidden
        />

        {/* Phone Dynamic Island */}
        <div
          className="pointer-events-none absolute left-1/2 top-[0.55rem] z-30 h-[0.7rem] w-[28%] -translate-x-1/2 rounded-full bg-black shadow-[0_0_0_1px_rgba(255,255,255,0.06)] sm:h-[0.8rem] md:hidden"
          aria-hidden
        />

        {/* Screen */}
        <div className="relative h-full w-full overflow-hidden rounded-[calc(var(--frame-r)-var(--frame-pad))] bg-background">
          <div className="relative z-0 h-full w-full">{children}</div>
        </div>

        {/* Phone side buttons */}
        <div
          className="pointer-events-none absolute -left-[1px] top-[22%] h-7 w-[2px] rounded-l-sm bg-zinc-600/90 md:hidden"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -left-[1px] top-[32%] h-10 w-[2px] rounded-l-sm bg-zinc-600/90 md:hidden"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[1px] top-[28%] h-14 w-[2px] rounded-r-sm bg-zinc-600/90 md:hidden"
          aria-hidden
        />
      </div>
    </div>
  );
}
